import Modal from 'react-modal';
import { X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { extendBuyingCredit } from '../../../api/orderAPI';
import { toast } from 'react-toastify';
import { formatCurrency } from '../../../utils/FormatCurrency';
import { useState } from 'react';
import { useLocation } from 'react-router-dom';

export const BVNUpload = ({ onUpgrade, closeModal, modalIsOpen }) => {
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const location = useLocation()
  const { mutate: extendMutate, isPending: extending } = useMutation({
    mutationFn: extendBuyingCredit,
    onSuccess: (res) => {
      if (res?.success) {
        setSuccessModalIsOpen(true);
        closeModal();
        if (location?.pathname === '/') {
         toast.success(
           'Your request has been submitted. We will contact you via email with the next steps.'
         )
       }
        onUpgrade();
      } else {
        toast.error(res?.message || 'Something went wrong.');
      }
    },
    onError: (error) => {
      toast.error(error?.message || 'Request failed. Please try again.');
    },
  });

  const handleProceed = () => extendMutate();

  return (
    <>
      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        shouldCloseOnOverlayClick={false}
        contentLabel="Extend Buying Credit Modal"
        overlayClassName="bg-black/50 fixed inset-0 flex justify-center items-center z-50"
        className="relative w-[376px] lg:w-[479px] rounded-[10px] bg-white outline-none border mx-2 lg:mx-0 px-6 md:px-8 py-8"
      >
        {/* Close button */}
        <button
          className="absolute top-4 right-4 bg-black text-white rounded-full p-1 hover:bg-gray-800 transition"
          onClick={closeModal}
          aria-label="Close Modal"
        >
          <X size={18} />
        </button>

        <div className="flex flex-col w-full">
          <div className="flex items-center justify-start mb-4">
            <h2 className="text-lg font-semibold mr-2">Upgrade to Tier 2</h2>
            <img
              src="/images/tier-verify.svg"
              alt="Tier verification"
              className="w-4 h-4"
            />
          </div>

          <p className="text-sm text-gray-600 mb-6">
            To upgrade to Tier 2 and increase your instalment limit to{' '}
            <span className="font-bold">{formatCurrency(1000000)}</span>, please click the request button and our
            team will reach out. Upon review, a status email will be sent within
            1 hour during work hours (8am–5pm). Requests submitted outside work
            hours may take more than 1 hour to process.
          </p>

          <div className="flex justify-between items-center w-full">
            <button
              type="button"
              onClick={closeModal}
              className="text-sm underline text-gray-700 hover:text-gray-900 transition"
            >
              Cancel
            </button>

            <button
              onClick={handleProceed}
              disabled={extending}
              className="bg-[var(--yellow-primary)] rounded-[5px] px-4 py-2 hover:opacity-90 disabled:opacity-50 transition"
              type="button"
            >
              {extending ? 'Requesting...' : 'Request'}
            </button>
          </div>
        </div>
      </Modal>
      {successModalIsOpen && (
        <SuccessModal
          isOpen={successModalIsOpen}
          onClose={() => setSuccessModalIsOpen(false)}
        />
      )}
    </>
  );
};

const SuccessModal = ({ isOpen, onClose }) => {
  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Success Modal"
      overlayClassName="bg-black/50 fixed inset-0 flex items-center justify-center z-[60]"
      className="relative w-[320px] lg:w-[420px] rounded-[10px] bg-white outline-none border mx-2 lg:mx-0 px-6 md:px-8 py-8"
      preventScroll={true}
    >
      <button
        className="absolute top-4 right-8 bg-black text-white rounded-full p-1 hover:bg-gray-800 transition"
        onClick={onClose}
        aria-label="Close Modal"
      >
        <X size={18} />
      </button>
      <div className="flex flex-col items-start ">
        <h3 className="text-lg font-semibold mb-4">Request Submitted</h3>
        <p className="text-sm">
          We've received your request to upgrade to Tier 2.
        </p>
        <p className="text-sm mb-6">
          We'll contact you via email with the next steps.
        </p>
        <div className="w-full flex justify-end">
          <button
            onClick={onClose}
            className="bg-[var(--yellow-primary)] rounded-[5px] px-6 py-2 hover:opacity-90 disabled:opacity-50 transition"
            type="button"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};
